// components/animal/FamilyTree.jsx
import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Tooltip,
  useTheme,
  Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/**
 * NEW: pass `allAnimals` (the full list, or a lite version) in addition to parents/currentAnimal.
 * allAnimals[] needs: id, tag_number, alias, breed, gender, dob, dam_id, sire_id
 */
const FamilyTree = ({ parents, currentAnimal, allAnimals = [] }) => {
  const theme = useTheme();

  // --- index animals by parentId for fast descendant walks ---
  const childrenByParent = useMemo(() => {
    const map = new Map();
    for (const a of allAnimals) {
      for (const pid of [a?.dam_id, a?.sire_id]) {
        if (!pid) continue;
        if (!map.has(pid)) map.set(pid, []);
        map.get(pid).push(a);
      }
    }
    return map;
  }, [allAnimals]);

  // --- gather all descendants (not just direct children) ---
  const offspring = useMemo(() => {
    if (!currentAnimal?.id) return [];
    const result = [];
    const seen = new Set();
    const stack = [currentAnimal.id];

    while (stack.length) {
      const pid = stack.pop();
      const kids = childrenByParent.get(pid) || [];
      for (const child of kids) {
        if (seen.has(child.id)) continue;
        seen.add(child.id);
        result.push(child);
        stack.push(child.id);
      }
    }
    // Optional: sort by DOB (youngest last) or tag_number
    result.sort((a, b) => (a.dob || "").localeCompare(b.dob || ""));
    return result;
  }, [currentAnimal?.id, childrenByParent]);

  return (
    <Box sx={{ width: "100%", overflowX: "auto", py: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: "fit-content",
        }}
      >
        {/* Parents */}
        <Box
          sx={{
            display: "flex",
            mb: 2,
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {parents?.dam && (
            <FamilyMemberCard animal={parents.dam} relationship="Dam" />
          )}
          {parents?.sire && (
            <FamilyMemberCard animal={parents.sire} relationship="Sire" />
          )}
        </Box>

        {/* Connector */}
        <Box
          sx={{
            height: 40,
            position: "relative",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              height: "100%",
              width: "2px",
              bgcolor: "divider",
            }}
          />
        </Box>

        {/* Current animal */}
        <Box sx={{ mb: 2 }}>
          <Tooltip title="Current animal">
            <Paper
              elevation={3}
              sx={{
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: 140,
                bgcolor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.dark,
                  mb: 1,
                  width: 44,
                  height: 44,
                }}
              >
                {currentAnimal.alias?.charAt(0) ||
                  currentAnimal.tag_number?.charAt(0) ||
                  "#"}
              </Avatar>
              <Typography variant="subtitle2" noWrap>
                {currentAnimal.alias || currentAnimal.tag_number}
              </Typography>
              <Typography variant="caption" noWrap>
                {currentAnimal.breed}
              </Typography>
            </Paper>
          </Tooltip>
        </Box>

        {/* Connector to descendants */}
        {offspring.length > 0 && (
          <>
            <Box
              sx={{
                height: 40,
                position: "relative",
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  height: "100%",
                  width: "2px",
                  bgcolor: "divider",
                }}
              />
            </Box>

            {/* Descendants shelf */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 1,
              }}
            >
              {offspring.map((desc) => (
                <FamilyMemberCard
                  key={desc.id}
                  animal={desc}
                  relationship="Offspring"
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

const FamilyMemberCard = ({ animal, relationship }) => {
  const safeDOB = animal?.dob ? new Date(animal.dob).toLocaleDateString() : "—";

  return (
    <Tooltip
      title={
        <>
          <Typography>
            {relationship}: {animal.alias || animal.tag_number}
          </Typography>
          <Typography variant="caption">Breed: {animal.breed}</Typography>
          <br />
          <Typography variant="caption">DOB: {safeDOB}</Typography>
        </>
      }
      arrow
    >
      <Paper
        elevation={2}
        sx={{
          p: 1,
          m: 0.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: 100,
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
        component={Link}
        to={`/animal/${animal.id}`}
      >
        <Avatar sx={{ width: 32, height: 32, mb: 0.5, fontSize: "0.875rem" }}>
          {animal.alias?.charAt(0) || animal.tag_number?.charAt(0) || "?"}
        </Avatar>
        <Typography variant="caption" noWrap sx={{ maxWidth: "100%" }}>
          {relationship}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: "0.7rem" }} noWrap>
          {animal.alias || animal.tag_number}
        </Typography>
        <Chip
          label={animal.gender}
          size="small"
          color={animal.gender === "Female" ? "secondary" : "primary"}
          sx={{ mt: 0.5, height: 20, fontSize: "0.65rem" }}
        />
      </Paper>
    </Tooltip>
  );
};

export default FamilyTree;
